/* eslint-disable react/destructuring-assignment, no-nested-ternary */
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { cloneChildComponents } from '../../utils/utils'
import { Spinner } from '../Spinner'
import './GenericPortlet.module.scss'
import { Portlet, PortletBody, PortletFooter, PortletHeader, PortletHeaderToolbar } from './Portlet'

const propTypes = {
  /**
   * Loading state from parent component. When true, displays a loading spinner in the portlet
   */
  isLoading: PropTypes.bool,
  /**
   * Error state from parent component. When true, displays "No data available" in the portlet
   */
  isError: PropTypes.bool,
  /**
   * Applies the portlet--height-fluid class to the porlet component (use along with Row align-items-stretch to fill row container)
   */
  fluidHeight: PropTypes.bool,
  /**
   * Style object to apply to the div wrapped inside of the portlet
   */
  innerPortletWrapperStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Enables the portlet header
   */
  hasHeader: PropTypes.bool,
  /**
   * Class to apply to the portlet header
   */
  portletHeaderClass: PropTypes.string,
  /**
   * The title of the portlet header
   */
  portletTitle: PropTypes.string,
  /**
   * Determines whether or not to display an info tooltip in the header of the portlet component
   */
  showInfoTooltip: PropTypes.bool,
  /**
   * The translation of the tool tip info message from en.json
   */
  infoTooltipTextId: PropTypes.string,
  /**
   * Whether or not to display a horizontal line break at the base of the portlet header
   */
  showHorizontalHeaderBreak: PropTypes.bool,
  /**
   * The location of the tool tip within the header of the portlet component
   */
  infoTooltipPlacement: PropTypes.string,
  /**
   * Determines whether or not to display a view details button in the header of the portlet component
   */
  showViewDetails: PropTypes.bool,
  /**
   * unless showViewDetails === true) onClick handler for the view details button
   */
  onViewDetailsClick: PropTypes.func,
  /**
   * The number of components in the portlet body (required if displaying a footer component with < 3 body components as the footer component is always retrieved from the last index in the children array)
   */
  rowComponents: PropTypes.number,
  /**
   * (Required) The components to display within the row of the portlet body (maximum of 3) and the portlet footer
   */
  children: PropTypes.node.isRequired,
  /**
   * Determines whether or not the body of the portlet should split it's child components with a vertical line
   */
  showVerticalSeparators: PropTypes.bool,
  /**
   * The pixel height of the vertical line separators. By default it will fill its container
   */
  verticalSeparatorHeight: PropTypes.string,
  /**
   * If provided, this style object will be applied to the div wrapped around the portlet body.
   */
  portletBodyStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * A class for the portlet body to change styling as needed
   */
  portletBodyClassName: PropTypes.string,
  /**
   * The class to apply to the bootstrap row wrapper of the portlet body (eg. align-items-center, align-items-stretch, etc)
   */
  portletBodyRowClass: PropTypes.string,
  /**
   * A class to apply to the Column wrapped around the cloned child components
   */
  childColumnWrapperClass: PropTypes.string,
  /**
   * Enables a portlet footer component
   */
  hasFooter: PropTypes.bool,
  /**
   * Enables a button in the footer component
   */
  showFooterButton: PropTypes.bool,
  /**
   * onClick handler for footer button element
   */
  onFooterButtonClick: PropTypes.func,
  /**
   * Language translation identifier for text to display in footer button
   */
  footerButtonTextId: PropTypes.string,
  /**
   * Background color of portlet footer component
   */
  footerBackgroundColor: PropTypes.string,
  /**
   * Whether or not there is a table component passed as a child to display below the footer component
   */
  hasFooterTable: PropTypes.bool,
  /**
   * Shows (unhides) the footer below the table. This is generally enabled within the onFooterButtonClick
   */
  showTableBelowFooter: PropTypes.bool,
  /**
   * Class to apply to the div that wraps the cloned footer component
   */
  footerChildWrapperClass: PropTypes.string,
}

/**
 * Renders different types of portlets depending on the props.
 *  Currently supports up to 3 components in a single row, as well as a single component in the footer
 */
function GenericPortlet(props) {
  const { children, ...rest } = props ?? {}
  const {
    isLoading,
    isError,
    fluidHeight,
    innerPortletWrapperStyle,
    hasHeader,
    portletHeaderClass,
    portletTitle,
    showInfoTooltip,
    infoTooltipTextId,
    infoTooltipPlacement,
    showHorizontalHeaderBreak,
    showViewDetails,
    showGearIcon,
    onViewDetailsClick,
    rowComponents,
    showVerticalSeparators,
    verticalSeparatorHeight,
    portletBodyStyle,
    portletBodyClassName,
    portletBodyRowClass,
    childColumnWrapperClass,
    hasFooter,
    showFooterButton,
    onFooterButtonClick,
    footerButtonTextId,
    footerBackgroundColor,
    hasFooterTable,
    showTableBelowFooter,
    footerChildWrapperClass,
    noBackground,
  } = rest

  const [portletComponents, setPortletComponents] = useState([])
  const [footerTableComponent, setFooterTableComponent] = useState()

  // Handles change of children (components to display  in the portlet)
  useEffect(() => {
    if (!children) {
      return
    }

    // Ensure type of array for portletComponents and set the portletComponents state
    if (!Array.isArray(children)) {
      setPortletComponents([children])
    } else {
      setPortletComponents(children)

      // Set the expandable table component
      if (hasFooterTable) {
        // eslint-disable-next-line no-unsafe-optional-chaining
        setFooterTableComponent(children[children?.length - 1])
      }
    }
  }, [children]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Last child is the footer table when `hasFooterTable`; it must not share the header Row with KPI columns. */
  const portletChildCount = portletComponents.length
  let maxBodyRowIndex
  if (portletChildCount === 0) {
    maxBodyRowIndex = -1
  } else if (rowComponents != null && rowComponents !== '' && !Number.isNaN(Number(rowComponents))) {
    maxBodyRowIndex = Math.min(Number(rowComponents) - 1, portletChildCount - 1)
  } else if (hasFooterTable && portletChildCount >= 2) {
    maxBodyRowIndex = portletChildCount - 2
  } else {
    maxBodyRowIndex = portletChildCount - 1
  }

  return isLoading ? (
    <>
      {/* Loading State */}
      <Portlet
        className={clsx('Uic-GenericPortlet-borderBottomDark', noBackground && 'Uic-GenericPortlet-gpNoBackground')}
      >
        <Spinner />
      </Portlet>
    </>
  ) : isError ? (
    <>
      {/* Error State */}
      <Portlet
        className={clsx('Uic-GenericPortlet-borderBottomDark', noBackground && 'Uic-GenericPortlet-gpNoBackground')}
      >
        <PortletBody>
          <div className="Uic-GenericPortlet-noDataLabel">
            <FormattedMessage id="UIC.GENERAL.NO_DATA" />
          </div>
        </PortletBody>
      </Portlet>
    </>
  ) : (
    <>
      {/* Success State */}
      <Portlet
        className={clsx('Uic-GenericPortlet-borderBottomDark', noBackground && 'Uic-GenericPortlet-gpNoBackground')}
        fluidHeight={fluidHeight}
      >
        <div style={innerPortletWrapperStyle}>
          {/* Header */}
          {hasHeader && (
            <PortletHeader
              className={portletHeaderClass}
              title={portletTitle !== undefined ? portletTitle : ''}
              toolbar={
                (showInfoTooltip || showViewDetails || showGearIcon) && (
                  <PortletHeaderToolbar>
                    <div className="Uic-GenericPortlet-tableActions">
                      {/* Info Icon */}
                      {showInfoTooltip && (
                        <OverlayTrigger
                          placement={infoTooltipPlacement || 'right'}
                          delay={{ show: 250, hide: 400 }}
                          overlay={
                            <Tooltip id="button-tooltip" {...props}>
                              <FormattedMessage id={infoTooltipTextId} />
                            </Tooltip>
                          }
                        >
                          <i className="fas fa-circle-info Uic-GenericPortlet-infoIcon" aria-hidden />
                        </OverlayTrigger>
                      )}

                      {/* View Details Button */}
                      {showViewDetails && (
                        <Button
                          id="pbm-view-details"
                          className="Uic-GenericPortlet-gpDetails"
                          variant="link"
                          onClick={onViewDetailsClick}
                        >
                          <FormattedMessage id="GENERAL.VIEW_DETAILS" />
                        </Button>
                      )}

                      {/* View Gear Icon */}
                      {showGearIcon && (
                        <Button
                          id="pbm-view-details"
                          className="Uic-GenericPortlet-gpDetails"
                          variant="link"
                          onClick={onViewDetailsClick}
                        >
                          <i className="fas fa-gear" style={{ fontSize: '14px', color: 'var(--bs-secondary-color)' }} />
                        </Button>
                      )}
                    </div>
                  </PortletHeaderToolbar>
                )
              }
            />
          )}

          {showHorizontalHeaderBreak && <hr className="m-0" />}

          {/* Body */}
          <div style={portletBodyStyle}>
            <PortletBody style={{ height: '100%' }} className={portletBodyClassName || ''}>
              <Row className={portletBodyRowClass} style={{ height: '100%' }}>
                {portletComponents.map((component, index) => {
                  if (index > maxBodyRowIndex) {
                    // Body row only shows KPI columns; extra children (e.g. footer table) render below.
                    return null
                  }
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <>
                      {/* Vertical Lines */}
                      {showVerticalSeparators && index !== 0 && (
                        <div style={{ height: verticalSeparatorHeight }} className="Uic-GenericPortlet-gpVertical" />
                      )}

                      {/* Child Components */}
                      <Col className={childColumnWrapperClass}>{cloneChildComponents(rest, [component], 1)}</Col>
                    </>
                  )
                })}
              </Row>
            </PortletBody>
          </div>

          {/* Footer */}
          {hasFooter && (
            <PortletFooter className="p-0">
              <div style={{ backgroundColor: footerBackgroundColor }}>
                <Row>
                  <div className="Uic-GenericPortlet-tableActions">
                    {showFooterButton && (
                      <div style={{ padding: '20px' }}>
                        <button type="button" className="Uic-GenericPortlet-gpButton" onClick={onFooterButtonClick}>
                          <FormattedMessage id={footerButtonTextId} />
                        </button>
                      </div>
                    )}
                  </div>
                </Row>
              </div>
            </PortletFooter>
          )}

          {/* Table Component Displaying Below Footer */}
          {showTableBelowFooter && footerTableComponent !== undefined && (
            <>
              <hr className="m-0" />
              <div className={footerChildWrapperClass}>
                <Row>
                  <Col sm="12" md="12" lg="12">
                    {cloneChildComponents({ ...rest, ...footerTableComponent.props }, [footerTableComponent], 1)}
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>
      </Portlet>
    </>
  )
}

GenericPortlet.propTypes = propTypes

export default GenericPortlet
